import React from 'react';
import { redirect } from 'react-router';

import { buildReactRouter } from 'view-components/lib/url';
import { UI, GLOBAL } from '../../../../config/routes/view-frontend';

import routeAuthentication from '../global/lib/routeAuthentication';
import App from '../global/components/App';
import NotFound from '../global/components/NotFound';
import Loading from '../global/components/Loading';

// react-router v8 replaced the v3 `onEnter` hook with route `loader`s. This
// adapts the existing `routeAuthentication(nextState, replace, callback)` (bound
// with the route's `metadata`) to a loader: it records any `replace` target and
// converts it into a v8 `redirect`, leaving the auth logic itself untouched.
const makeAuthLoader = (metadata: any) => async ({ params, request }: any) => {
  const url = new URL(request.url);
  const query: Record<string, any> = Object.fromEntries(url.searchParams.entries());
  const nextState = { params, location: { pathname: url.pathname, query } };

  let redirectTo: any = null;
  const replace = (to: any) => { redirectTo = to; };
  const done = () => {};

  await routeAuthentication.call({ metadata }, nextState, replace, done);

  if (redirectTo) {
    const target = typeof redirectTo === 'string'
      ? redirectTo
      : (redirectTo.pathname || '/');
    return redirect(target);
  }

  return null;
};

import testRoutes from './testRoutes';

// react-loadable is unmaintained and not React 19 friendly; use React.lazy.
// The loading fallback is provided by the <Suspense> boundary in App (around the
// router Outlet), so it no longer needs to be specified per component.
const Loadable = (opts: { loader: () => Promise<any> }) => React.lazy(opts.loader);

const Login = Loadable({
  loader: () => import('../features/Authentication/Login.proxy'),
});
const Logout = Loadable({
  loader: () => import('../features/Authentication/Logout.proxy'),
});
const ResetPassword = Loadable({
  loader: () => import('../features/Authentication/ResetPassword.proxy'),
});
const ChangePassword = Loadable({
  loader: () => import('../features/Authentication/ChangePassword.proxy'),
});
const ChangeUserPassword = Loadable({
  loader: () => import('../features/Users/ChangeUserPassword.proxy'),
});

const AccountSummary = Loadable({
  loader: () => import('../features/AccountSummary/AccountSummary.proxy'),
});

const SearchesIndex = Loadable({
  loader: () => import('../features/Searches/SavedSearchList.proxy'),
});
const SearchesSave = Loadable({
  loader: () => import('../features/Searches/SaveCurrentSearch/SaveCurrentSearch.proxy'),
});
const SearchesEdit = Loadable({
  loader: () => import('../features/Searches/SavedSearchEdit.proxy'),
});
const SearchesResultsIndex = Loadable({
  loader: () => import('../features/Searches/SavedSearchResults.proxy'),
});
const SearchesResultsAsset = Loadable({
  loader: () => import('../features/Searches/SavedSearchAssetView.proxy'),
});

const ResultsIndex = Loadable({
  loader: () => import('../features/Searches/SearchResults.proxy'),
});
const ResultsAssets = Loadable({
  loader: () => import('../features/Searches/RenderedAsset.proxy'),
});

const NewSearchIndex = Loadable({
  loader: () => import('../features/Searches/NewSearchResults.proxy'),
});
const NewSearchSave = Loadable({
  loader: () => import('../features/Searches/SaveNewSearch/SaveNewSearch.proxy'),
});
const NewSearchEdit = Loadable({
  loader: () => import('../features/Searches/UnsavedSearchEdit.proxy'),
});
const NewSearchResultsIndex = Loadable({
  loader: () => import('../features/Searches/UnsavedSearchResults.proxy'),
});
const NewSearchResultsAsset = Loadable({
  loader: () => import('../features/Searches/UnsavedSearchRenderedAsset.proxy'),
});

const Exports = Loadable({
  loader: () => import('../features/Exports/Exports.proxy'),
});

const Jobs = Loadable({
  loader: () => import('../features/Jobs/Jobs.proxy'),
});

const HoldReasonsIndex = Loadable({
  loader: () => import('../features/HoldReasons/HoldReasons.proxy'),
});
const HoldReasonsNew = Loadable({
  loader: () => import('../features/HoldReasons/NewHoldReason.proxy'),
});
const HoldReasonsShowIndex = Loadable({
  loader: () => import('../features/HoldReasons/Assets/HoldReasonAssets.proxy'),
});
const HoldReasonsShowAssets = Loadable({
  loader: () => import('../features/HoldReasons/Assets/HoldReasonRenderedAsset.proxy'),
});

const UsersIndex = Loadable({
  loader: () => import('../features/Users/Users.proxy'),
});
const UsersNew = Loadable({
  loader: () => import('../features/Users/NewUser.proxy'),
});
const UsersEdit = Loadable({
  loader: () => import('../features/Users/EditUser.proxy'),
});

const MyArchiveSplitViewIndex = Loadable({
  loader: () => import('../features/EndUser/MyArchive.proxy'),
});

const SettingsIndex = Loadable({
  loader: () => import('../features/Settings/Settings'),
});

const SettingsCollectorsIndex = Loadable({
  loader: () => import('../features/Settings/Collectors.proxy'),
});
const SettingsCollectorsEmail = Loadable({
  loader: () => import('../features/Settings/Email.proxy'),
});

const SettingsPolicies = Loadable({
  loader: () => import('../features/Settings/Policies.proxy'),
});

const SettingsReportsIndex = Loadable({
  loader: () => import('../features/Settings/Reports.proxy'),
});
const SettingsReportsAuditsNew = Loadable({
  loader: () => import('../features/Settings/Reports/NewAuditReport.proxy'),
});
const SettingsReportsReportsEdit = Loadable({
  loader: () => import('../features/Settings/Reports/EditReport.proxy'),
});

const SettingsSearchListsIndex = Loadable({
  loader: () => import('../features/Settings/SearchLists.proxy'),
});
const SettingsSearchListsNew = Loadable({
  loader: () => import('../features/Settings/NewSearchList.proxy'),
});
const SettingsSearchListsEdit = Loadable({
  loader: () => import('../features/Settings/EditSearchList.proxy'),
});

const SettingsAccountSettings = Loadable({
  loader: () => import('../features/Settings/AccountSettings.proxy'),
});

const SettingsSecurity = Loadable({
  loader: () => import('../features/Settings/Security.proxy'),
});

const SettingsO365Integration = Loadable({
  loader: () => import('../features/Settings/components/O365Integration.proxy'),
});

const SettingsSingleSignOn = Loadable({
  loader: () => import('../features/Settings/SingleSignOn'),
});

const SettingsAdSyncIndex = Loadable({
  loader: () => import('../features/Settings/ADSync.proxy'),
});
const SettingsAdSyncAdWizard = Loadable({
  loader: () => import('../features/Settings/ADWizard.proxy'),
});
const SettingsAdSyncAdSuccess = Loadable({
  loader: () => import('../features/Settings/ADSuccess.proxy'),
});
const SettingsAdSyncSyncLog = Loadable({
  loader: () => import('../features/Settings/ADSyncLog.proxy'),
});
const SettingsAdSyncSyncConflicts = Loadable({
  loader: () => import('../features/Settings/ADSyncConflicts.proxy'),
});
const SettingsAdSyncAttributeMappings = Loadable({
  loader: () => import('../features/Settings/ADSyncAttributeMappings.proxy'),
});
const SettingsAdSyncSourceAdGroups = Loadable({
  loader: () => import('../features/Settings/ADSyncSourceADGroups.proxy'),
});
const SettingsAdSyncAppCredentials = Loadable({
  loader: () => import('../features/Settings/ADSyncAppCredentials.proxy'),
});

const routeHandlers = process.env.NODE_ENV === 'test' ? { ...testRoutes } : {
  Login,
  Logout,
  ResetPassword,
  ChangePassword,
  ChangeUserPassword,

  Dashboard: AccountSummary,

  SearchesIndex,
  SearchesShow: SearchesSave,
  SearchesEdit,
  SearchesResultsIndex,
  SearchesResultsAsset,

  ResultsIndex,
  ResultsAssets,
  ResultsSplitViewAssets: ResultsIndex,

  NewSearchIndex,
  NewSearchSave,
  NewSearchEdit,
  NewSearchResultsIndex,
  NewSearchResultsAsset,

  Exports,

  Jobs,

  HoldReasonsIndex,
  HoldReasonsNew,
  HoldReasonsShowIndex,
  HoldReasonsShowAssets,

  UsersIndex,
  UsersNew,
  UsersEdit,

  MyArchiveSplitViewIndex,
  MyArchiveSplitViewAsset: MyArchiveSplitViewIndex,

  SettingsIndex,
  SettingsCollectorsIndex,
  SettingsCollectorsEmail,
  SettingsPolicies,
  SettingsReportsIndex,
  SettingsReportsAuditsNew,
  SettingsReportsReportsEdit,
  SettingsSearchListsIndex,
  SettingsSearchListsNew,
  SettingsSearchListsEdit,
  SettingsAdSyncIndex,
  SettingsAdSyncAdWizard,
  SettingsAdSyncAdSuccess,
  SettingsAdSyncSyncLog,
  SettingsAdSyncSyncConflicts,
  SettingsAdSyncAttributeMappings,
  SettingsAdSyncSourceAdGroups,
  SettingsAdSyncAppCredentials,
  SettingsAccountSettings,
  SettingsSecurity,
  SettingsO365Integration,
  SettingsSingleSignOn,
  // create_settings_section[keys]: new keys go above here
};

const siteName = ':site_name';
const appEntryRoute = `${siteName}/${GLOBAL.APP_NAME}`;

// TODO: redo all the layouts so they come from here, not from the
// individual components themselves.
[
  routeHandlers.Jobs,
  routeHandlers.SearchesIndex,
  routeHandlers.SearchesShow,
  routeHandlers.NewSearchSave,
  routeHandlers.UsersIndex,
  routeHandlers.SettingsPolicies,
  routeHandlers.MyArchiveSplitViewIndex,
  routeHandlers.ResultsIndex,
  routeHandlers.Dashboard,
  routeHandlers.HoldReasonsIndex,
  routeHandlers.HoldReasonsNew,
  routeHandlers.HoldReasonsShowIndex,
].forEach((_view) => {
  const view = _view;
  view.isNewLayoutCompatible = true;
});

// Redirect the app-entry / bare-site URLs to login via a route LOADER rather
// than a <Navigate> element. The <App> layout renders <DomainInfoLoading/>
// (not its <Outlet/>) until domain-info has loaded, and these redirect routes
// have no loader to trigger that load -- so a <Navigate> rendered inside the
// Outlet never runs and the page hangs on "Domain info loading..." forever
// (SNAR-9852). A loader runs before <App> paints and issues the redirect
// unconditionally, landing the user on the real login route whose loader then
// loads domain-info normally.
const redirectToLoginLoader = async ({ params }: any) => {
  const site = params.site_name ?? '';
  // Validate the site segment before building the redirect target so a crafted
  // param can't turn this into an open redirect (e.g. a protocol-relative
  // //evil.com target). Site names are simple slugs; anything else is a 404.
  if (!/^[A-Za-z0-9_-]+$/.test(site)) {
    throw new Response('Not Found', { status: 404 });
  }
  return redirect(`/${encodeURIComponent(site)}/${GLOBAL.APP_NAME}/login`);
};

const routes = [
  {
    // Pathless layout route (was `<Route component={App}>`). App renders an
    // <Outlet/>; page-view monitoring moved into App itself (was the root onEnter).
    element: <App />,
    // Data routers run route loaders (makeAuthLoader) before the first paint;
    // react-router renders this during that initial-hydration window instead of
    // warning + rendering nothing ("No HydrateFallback element provided").
    HydrateFallback: Loading,
    children: [
      {
        path: appEntryRoute,
        children: [
          // Visiting the app entry point exactly (`:site_name/ui`) redirects to
          // login. Uses a loader (not <Navigate>) so it fires before <App>
          // withholds its Outlet behind the domain-info spinner (SNAR-9852).
          { index: true, loader: redirectToLoginLoader, element: <Loading /> },
          ...buildReactRouter(UI, routeHandlers, { makeLoader: makeAuthLoader }),
        ],
      },
      // Bare `:site_name` redirects into the app's login. Same loader-based
      // redirect so it isn't trapped behind the domain-info spinner (SNAR-9852).
      { path: siteName, loader: redirectToLoginLoader, element: <Loading /> },
    ],
  },
  { path: '*', element: <NotFound /> },
];

export default routes;
